import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    CardSurfaceConstantType,
    CardColorsConstantType,
    CardInsetConstantType,
    CardDynamicStyleType,
    CardVariant,
    CardColors,
    CardSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    type ElevationLevelType,
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const CARD_SIZE_INSETS: Record<CardSize, keyof NCoreUIKit.Spaces> = {
    medium: "spacingLg",
    small: "spacingMd"
};

export const CARD_VARIANT_ELEVATIONS: Record<CardVariant, ElevationLevelType> = {
    elevated: "level2",
    outline: "level1",
    filled: "level0",
    inset: "level0"
};

export const CARD_SURFACE_COLORS: Record<CardVariant, keyof NCoreUIKit.ContainerContentColors> = {
    elevated: "raised",
    outline: "raised",
    filled: "subtle",
    inset: "mid"
};

export const getCardInset = ({
    spaces,
    size
}: CardInsetConstantType): number => {
    return spaces[CARD_SIZE_INSETS[size]];
};

export const getCardColors = ({
    isDisabled,
    colors
}: CardColorsConstantType): CardColors => {
    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        return {
            descriptionColor: disabledColor,
            titleColor: disabledColor
        };
    }

    return {
        descriptionColor: colors.content.text.mid,
        titleColor: colors.content.text.high
    };
};

export const getCardSurfaceStyle = ({
    shadowAlpha,
    elevations,
    isDisabled,
    borders,
    variant,
    colors
}: CardSurfaceConstantType): ViewStyle => {
    if(isDisabled) {
        return {
            backgroundColor: colors.system.state.overlay.disabled.neutral,
            borderColor: colors.system.state.border.disabled.neutral,
            borderWidth: borders.line
        };
    }

    if(variant === "filled") {
        return {
            backgroundColor: colors.content.container.subtle,
            borderWidth: borders.line,
            borderColor: "transparent"
        };
    }

    if(variant === "inset") {
        return {
            backgroundColor: colors.content.container.mid,
            borderColor: colors.content.border.subtle,
            borderWidth: borders.line
        };
    }

    return getSurfaceStyle({
        level: CARD_VARIANT_ELEVATIONS[variant],
        surface: "card",
        shadowAlpha,
        elevations,
        borders,
        colors
    });
};

const stylesheet = StyleSheet.create({
    status: {
        pointerEvents: "none",
        position: "absolute",
        overflow: "hidden",
        ...webStyle({
            clipPath: "inset(50%)",
            whiteSpace: "nowrap"
        })
    },
    layer: {
        ...StyleSheet.absoluteFill,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    surface: {
        flexDirection: "column",
        borderStyle: "solid",
        minWidth: 0
    },
    stretched: {
        alignSelf: "stretch"
    }
});

export const useStyles = ({
    spreadBehaviour,
    isFocusVisible,
    isFirstFlush,
    shapeConfigs,
    isLastFlush,
    isPressable,
    shadowAlpha,
    elevations,
    isDisabled,
    isLoading,
    radiuses,
    borders,
    variant,
    colors,
    inset
}: CardDynamicStyleType) => {
    const styles = {
        surface: {
            ...getCardSurfaceStyle({
                shadowAlpha,
                elevations,
                isDisabled,
                borders,
                variant,
                colors
            }),
            ...getCornerStyle({
                radius: radiuses.surface,
                shapeConfigs
            }),
            paddingBottom: inset,
            paddingTop: inset,
            gap: inset
        } as Mutable<ViewStyle>,
        layer: {
            ...getCornerStyle({
                radius: radiuses.surface,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        status: {
            height: borders.line,
            width: borders.line
        } as Mutable<ViewStyle>
    };

    if(isFirstFlush) {
        delete styles.surface.paddingTop;
    }

    if(isLastFlush) {
        delete styles.surface.paddingBottom;
    }

    Object.assign(styles.surface, getFocusRingStyle({
        isFocusVisible: isPressable && isFocusVisible,
        borders,
        colors
    }));

    if(spreadBehaviour === "stretch") {
        Object.assign(styles.surface, stylesheet.stretched);
    }

    if(isDisabled) {
        Object.assign(styles.surface, webStyle({
            cursor: "not-allowed"
        }));
    }

    if(isLoading) {
        Object.assign(styles.layer, webStyle({
            cursor: "not-allowed"
        }));
    }

    return styles;
};
export default stylesheet;
